// =============================================================================
// SIREPPAR — Documentación del expediente cuando es de tipo rendición:
//  - Bloque 1: Versiones de la rendición. Cada versión muestra hash + estado
//    de revisión del PDF firmado vinculado y permite aprobar / rechazar /
//    volver a pendiente (igual que en /documentos). Las versiones en estado
//    borrador no son aprobables.
//  - Bloque 2: Otros documentos del expediente (adjuntos a respuestas de
//    observaciones, notas, etc.). Excluye PDFs firmados (ya están en bloque 1).
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { documentoService, rendicionAnualService, rendicionCampanaService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const ROLES_REVISION = ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA'];

// Tipos de documento que son "PDF firmado" de la rendición — no aparecen en
// el bloque 2 (se muestran en el bloque 1 junto a su versión).
const TIPOS_PDF_FIRMADO = ['CERTIFICACION_CPCE', 'COPIA_SPECA', 'INFORME_FINANCIERO'];

interface ExpedienteLite {
  id: string;
  tipo: string;
  datosFormulario?: {
    rendicionAnualId?: string;
    rendicionCampanaId?: string;
    [k: string]: any;
  } | null;
}

interface VersionFila {
  id: string;
  numeroVersion: number;
  hashCode: string;
  pdfGeneradoUrl: string | null;
  fechaGeneracion: string;
  documentoVinculadoId: string | null;
  esBorrador?: boolean;
  usuario?: { nombre?: string; apellido?: string; email?: string } | null;
}

interface DocumentoFila {
  id: string;
  tipo: string;
  titulo: string;
  nombreOriginal: string;
  estadoRevision: string;
  observacion?: string | null;
  revisadoPorNombre?: string | null;
  revisadoEn?: string | null;
  creadoEn: string;
}

const TIPO_DOCUMENTO_LABEL: Record<string, string> = {
  ACTA_CONSTITUTIVA: 'Acta Constitutiva',
  CARTA_ORGANICA: 'Carta Orgánica',
  PLATAFORMA_ELECTORAL: 'Plataforma Electoral',
  DECLARACION_PRINCIPIOS: 'Declaración de Principios',
  ACTA_ASAMBLEA: 'Acta de asamblea donde se proclamaron las autoridades',
  PUBLICACION_LLAMADO_ELECCIONES: 'Publicación de llamado a elecciones',
  CRONOGRAMA_ELECCIONES: 'Cronograma de elecciones',
  PADRON_AFILIADOS: 'Padrón de Afiliados',
  PADRON_AFILIADOS_PDF: 'Padrón de Afiliados (PDF)',
  BALANCE: 'Balance',
  RESOLUCION: 'Resolución',
  NOTA: 'Nota',
  DICTAMEN: 'Dictamen',
  INFORME_PERICIAL: 'Informe pericial',
  COMPROBANTE: 'Comprobante',
  COPIA_SPECA: 'Copia SPECA',
  CERTIFICACION_CPCE: 'Certificación CPCECh',
  INFORME_FINANCIERO: 'Informe Financiero',
  OTRO: 'Otro',
};

function formatFecha(f: string): string {
  return new Date(f).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ESTADOS = ['PENDIENTE', 'APROBADO', 'RECHAZADO'] as const;
type EstadoRevision = (typeof ESTADOS)[number];

function badgeEstadoClasses(estado: string): string {
  if (estado === 'APROBADO') return 'bg-green-100 text-green-800';
  if (estado === 'RECHAZADO') return 'bg-red-100 text-red-800';
  return 'bg-amber-100 text-amber-800';
}

const ESTADO_LABEL: Record<EstadoRevision, string> = {
  APROBADO: 'Verificado',
  RECHAZADO: 'Rechazado',
  PENDIENTE: 'Pendiente',
};

// Badge desplegable: si el documento ya está APROBADO o RECHAZADO, al hacer click
// muestra las otras dos opciones para cambiarlo. Mismo patrón que /documentos.
function EstadoBadgeDropdown({
  estado,
  onSelect,
}: {
  estado: EstadoRevision;
  onSelect: (e: EstadoRevision) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const opciones = ESTADOS.filter((e) => e !== estado);

  return (
    <span ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity ${badgeEstadoClasses(estado)}`}
      >
        {ESTADO_LABEL[estado]} ▾
      </button>
      {open && (
        <span className="absolute z-20 top-7 left-0 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px]">
          {opciones.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => {
                onSelect(e);
                setOpen(false);
              }}
              className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${badgeEstadoClasses(e)}`}
            >
              {ESTADO_LABEL[e]}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

export default function VersionesPdfList({ expediente }: { expediente: ExpedienteLite }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const puedeRevisar = ROLES_REVISION.includes(user?.rol || '');

  const esAnual = expediente.tipo === 'RENDICION_ANUAL';
  const rendicionId = esAnual
    ? expediente.datosFormulario?.rendicionAnualId
    : expediente.datosFormulario?.rendicionCampanaId;

  const service = esAnual ? rendicionAnualService : rendicionCampanaService;
  const queryKey = esAnual ? 'rendicion-anual-versiones-doc' : 'rendicion-campana-versiones-doc';

  const { data: versiones, isLoading: loadingVersiones } = useQuery({
    queryKey: [queryKey, rendicionId],
    queryFn: async () => {
      const res = await service.listVersiones(rendicionId!);
      return (res.data ?? []) as VersionFila[];
    },
    enabled: !!rendicionId,
  });

  const { data: docsResp, isLoading: loadingDocs } = useQuery({
    queryKey: ['expediente-documentos', expediente.id],
    queryFn: async () => {
      const res = await documentoService.list({ expedienteId: expediente.id, pageSize: 200 });
      const rows = (res.data?.data?.items ?? res.data?.data ?? []) as any[];
      // La verificación de los PDFs firmados de rendición corresponde a la
      // dimensión de admisibilidad: la proyectamos sobre estadoRevision/revisado*.
      return rows.map((d) => ({
        ...d,
        estadoRevision: d.estadoRevisionAdmisibilidad ?? d.estadoRevision,
        revisadoPorNombre: d.admisibilidadRevisadoPor ?? d.revisadoPorNombre,
        revisadoEn: d.admisibilidadRevisadoEn ?? d.revisadoEn,
      })) as DocumentoFila[];
    },
  });

  // Mapeo versión → Documento del PDF firmado (por documentoVinculadoId).
  const docsByVersionId = useMemo(() => {
    const map = new Map<string, DocumentoFila>();
    if (!versiones || !docsResp) return map;
    versiones.forEach((v) => {
      if (v.documentoVinculadoId) {
        const doc = docsResp.find((d) => d.id === v.documentoVinculadoId);
        if (doc) map.set(v.id, doc);
      }
    });
    return map;
  }, [versiones, docsResp]);

  // IDs de docs vinculados a versiones (para excluir del bloque 2).
  const idsEnVersiones = useMemo(() => {
    const set = new Set<string>();
    docsByVersionId.forEach((d) => set.add(d.id));
    return set;
  }, [docsByVersionId]);

  // Bloque 2: docs del expediente que NO sean PDFs firmados ni estén ya en versiones
  const docsBloque2 = useMemo(
    () =>
      (docsResp ?? []).filter(
        (d) => !idsEnVersiones.has(d.id) && !TIPOS_PDF_FIRMADO.includes(d.tipo),
      ),
    [docsResp, idsEnVersiones],
  );

  const revisarMutation = useMutation({
    mutationFn: async ({
      id,
      estado,
      observacion,
    }: {
      id: string;
      estado: EstadoRevision;
      observacion?: string | null;
    }) => documentoService.updateEstadoRevision(id, estado, observacion),
    onSuccess: () => {
      toast.success('Estado actualizado');
      queryClient.invalidateQueries({ queryKey: ['expediente-documentos', expediente.id] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.message ?? 'Error al revisar'),
  });

  if (!rendicionId) {
    return (
      <div className="bg-amber-50 border-l-4 border-amber-400 p-4 text-sm text-amber-900 rounded">
        El expediente no tiene rendición vinculada en sus datos.
      </div>
    );
  }

  async function descargarVersion(numeroVersion: number) {
    try {
      const res = await service.descargarVersionPdf(rendicionId!, numeroVersion);
      const url = res.data?.url;
      if (url) window.open(url, '_blank');
      else toast.error('No se obtuvo URL del PDF');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? 'No se pudo descargar');
    }
  }

  async function descargarDocumento(id: string) {
    try {
      const res = await documentoService.getUrl(id);
      const url = res.data?.data?.url ?? res.data?.url;
      if (url) window.open(url, '_blank');
      else toast.error('No se obtuvo URL');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? 'No se pudo descargar');
    }
  }

  return (
    <div className="space-y-4">
      {/* Bloque 1: Versiones de la rendición */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="border-b border-gray-200 pb-3 mb-4">
          <h3 className="text-base font-semibold text-gray-900">Versiones de la rendición</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            PDFs firmados con hash de integridad.
            {puedeRevisar && ' Validá cada PDF firmado o cambiá su estado.'}
          </p>
        </div>

        {loadingVersiones ? (
          <p className="text-sm text-gray-500 italic">Cargando versiones...</p>
        ) : !versiones || versiones.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            La rendición todavía no tiene versiones generadas.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Versión</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Generada</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Generada por</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hash</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {versiones.map((v) => {
                  const docVersion = docsByVersionId.get(v.id) ?? null;
                  return (
                    <FilaVersion
                      key={v.id}
                      version={v}
                      documento={docVersion}
                      puedeRevisar={puedeRevisar}
                      onDescargarVersion={() => descargarVersion(v.numeroVersion)}
                      onCambiarEstado={(estado, observacion) =>
                        docVersion &&
                        revisarMutation.mutate({ id: docVersion.id, estado, observacion })
                      }
                      revisando={revisarMutation.isPending}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bloque 2: Otros documentos del expediente */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="border-b border-gray-200 pb-3 mb-4">
          <h3 className="text-base font-semibold text-gray-900">Otros documentos del expediente</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Adjuntos a respuestas de observaciones, notas y otros documentos vinculados al expediente.
            {puedeRevisar && ' Validá cada documento o cambiá su estado.'}
          </p>
        </div>

        {loadingDocs ? (
          <p className="text-sm text-gray-500 italic">Cargando documentos...</p>
        ) : docsBloque2.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No hay otros documentos adjuntos al expediente.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subido</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {docsBloque2.map((d) => (
                  <FilaDocumento
                    key={d.id}
                    documento={d}
                    puedeRevisar={puedeRevisar}
                    onDescargar={() => descargarDocumento(d.id)}
                    onCambiarEstado={(estado, observacion) =>
                      revisarMutation.mutate({ id: d.id, estado, observacion })
                    }
                    revisando={revisarMutation.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Fila de versión: muestra hash + estado del documento firmado y permite
// cambiar el estado a APROBADO/RECHAZADO/PENDIENTE (igual a /documentos).
// Las versiones en estado borrador no son aprobables.
// =============================================================================

function FilaVersion({
  version,
  documento,
  puedeRevisar,
  onDescargarVersion,
  onCambiarEstado,
  revisando,
}: {
  version: VersionFila;
  documento: DocumentoFila | null;
  puedeRevisar: boolean;
  onDescargarVersion: () => void;
  onCambiarEstado: (estado: EstadoRevision, observacion?: string | null) => void;
  revisando: boolean;
}) {
  const [mostrarRechazo, setMostrarRechazo] = useState(false);
  const [motivo, setMotivo] = useState('');

  const esBorrador = !!version.esBorrador;
  const estadoActual = !esBorrador && documento ? documento.estadoRevision : null;

  function cambiar(nuevo: EstadoRevision) {
    if (!documento) return;
    if (nuevo === 'RECHAZADO') {
      setMostrarRechazo(true);
      return;
    }
    onCambiarEstado(nuevo, null);
    setMostrarRechazo(false);
    setMotivo('');
  }

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-3 py-2 font-semibold">N° {version.numeroVersion}</td>
        <td className="px-3 py-2 text-gray-700">{formatFecha(version.fechaGeneracion)}</td>
        <td className="px-3 py-2 text-gray-700">
          {version.usuario
            ? `${version.usuario.nombre ?? ''} ${version.usuario.apellido ?? ''}`.trim() ||
              version.usuario.email ||
              '—'
            : '—'}
        </td>
        <td className="px-3 py-2">
          <HashCopiable hash={version.hashCode} />
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {/* Estado / acciones a la izquierda */}
            <div className="flex items-center gap-2">
              {esBorrador && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                  borrador
                </span>
              )}
              {!esBorrador && !documento && (
                <span className="text-xs text-gray-400 italic">Sin documento</span>
              )}
              {puedeRevisar && !esBorrador && documento && estadoActual === 'PENDIENTE' && (
                <>
                  <button
                    type="button"
                    onClick={() => cambiar('APROBADO')}
                    disabled={revisando}
                    className="text-xs border border-green-600 text-green-600 hover:bg-green-50 px-2.5 py-1 rounded-md transition-colors disabled:opacity-50"
                  >
                    Verificar
                  </button>
                  <button
                    type="button"
                    onClick={() => cambiar('RECHAZADO')}
                    disabled={revisando}
                    className="text-xs border border-red-600 text-red-600 hover:bg-red-50 px-2.5 py-1 rounded-md transition-colors disabled:opacity-50"
                  >
                    Rechazar
                  </button>
                </>
              )}
              {puedeRevisar && !esBorrador && documento && estadoActual && estadoActual !== 'PENDIENTE' && (
                <EstadoBadgeDropdown
                  estado={estadoActual as EstadoRevision}
                  onSelect={(nuevo) => cambiar(nuevo)}
                />
              )}
              {!puedeRevisar && !esBorrador && documento && estadoActual && (
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badgeEstadoClasses(estadoActual)}`}
                  title={
                    documento.revisadoPorNombre
                      ? `Revisado por ${documento.revisadoPorNombre}${
                          documento.revisadoEn ? ' el ' + formatFecha(documento.revisadoEn) : ''
                        }${documento.observacion ? '\nObservación: ' + documento.observacion : ''}`
                      : undefined
                  }
                >
                  {ESTADO_LABEL[estadoActual as EstadoRevision] ?? estadoActual}
                </span>
              )}
            </div>
            {/* Descargar a la derecha */}
            {version.pdfGeneradoUrl && (
              <button
                type="button"
                onClick={onDescargarVersion}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded"
              >
                Descargar PDF
              </button>
            )}
          </div>
        </td>
      </tr>
      {mostrarRechazo && documento && (
        <tr className="bg-red-50">
          <td colSpan={5} className="px-3 py-3">
            <label className="block text-xs font-semibold uppercase text-gray-700 mb-1">
              Motivo del rechazo <span className="text-red-600">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Indicá por qué el PDF no es válido..."
                className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  if (!motivo.trim()) return;
                  onCambiarEstado('RECHAZADO', motivo.trim());
                  setMostrarRechazo(false);
                  setMotivo('');
                }}
                disabled={!motivo.trim() || revisando}
                className="bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-3 py-1.5 rounded disabled:opacity-50"
              >
                Confirmar rechazo
              </button>
              <button
                type="button"
                onClick={() => {
                  setMostrarRechazo(false);
                  setMotivo('');
                }}
                className="bg-white border border-gray-300 text-gray-700 text-xs font-medium px-3 py-1.5 rounded hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// =============================================================================
// Fila de documento del bloque 2 (otros adjuntos del expediente)
// =============================================================================

function FilaDocumento({
  documento,
  puedeRevisar,
  onDescargar,
  onCambiarEstado,
  revisando,
}: {
  documento: DocumentoFila;
  puedeRevisar: boolean;
  onDescargar: () => void;
  onCambiarEstado: (estado: EstadoRevision, observacion?: string | null) => void;
  revisando: boolean;
}) {
  const [mostrarRechazo, setMostrarRechazo] = useState(false);
  const [motivo, setMotivo] = useState('');

  const estadoActual = documento.estadoRevision;

  function cambiar(nuevo: EstadoRevision) {
    if (nuevo === 'RECHAZADO') {
      setMostrarRechazo(true);
      return;
    }
    onCambiarEstado(nuevo, null);
    setMostrarRechazo(false);
    setMotivo('');
  }

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-3 py-2 text-gray-700 max-w-[300px] truncate" title={documento.titulo}>
          {documento.titulo}
        </td>
        <td className="px-3 py-2 text-xs text-gray-600">
          {TIPO_DOCUMENTO_LABEL[documento.tipo] ?? documento.tipo}
        </td>
        <td className="px-3 py-2 text-xs text-gray-600">{formatFecha(documento.creadoEn)}</td>
        <td className="px-3 py-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {/* Acciones de revisión a la izquierda */}
            <div className="flex items-center gap-2">
              {puedeRevisar && estadoActual === 'PENDIENTE' && (
                <>
                  <button
                    type="button"
                    onClick={() => cambiar('APROBADO')}
                    disabled={revisando}
                    className="text-xs border border-green-600 text-green-600 hover:bg-green-50 px-2.5 py-1 rounded-md transition-colors disabled:opacity-50"
                  >
                    Verificar
                  </button>
                  <button
                    type="button"
                    onClick={() => cambiar('RECHAZADO')}
                    disabled={revisando}
                    className="text-xs border border-red-600 text-red-600 hover:bg-red-50 px-2.5 py-1 rounded-md transition-colors disabled:opacity-50"
                  >
                    Rechazar
                  </button>
                </>
              )}
              {puedeRevisar && estadoActual !== 'PENDIENTE' && (
                <EstadoBadgeDropdown
                  estado={estadoActual as EstadoRevision}
                  onSelect={(nuevo) => cambiar(nuevo)}
                />
              )}
            </div>
            {/* Descargar a la derecha */}
            <button
              type="button"
              onClick={onDescargar}
              className="text-blue-600 hover:text-blue-800 text-xs font-medium"
            >
              Descargar
            </button>
          </div>
        </td>
      </tr>
      {mostrarRechazo && (
        <tr className="bg-red-50">
          <td colSpan={4} className="px-3 py-3">
            <label className="block text-xs font-semibold uppercase text-gray-700 mb-1">
              Motivo del rechazo <span className="text-red-600">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Indicá por qué el documento no es válido..."
                className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  if (!motivo.trim()) return;
                  onCambiarEstado('RECHAZADO', motivo.trim());
                  setMostrarRechazo(false);
                  setMotivo('');
                }}
                disabled={!motivo.trim() || revisando}
                className="bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-3 py-1.5 rounded disabled:opacity-50"
              >
                Confirmar rechazo
              </button>
              <button
                type="button"
                onClick={() => {
                  setMostrarRechazo(false);
                  setMotivo('');
                }}
                className="bg-white border border-gray-300 text-gray-700 text-xs font-medium px-3 py-1.5 rounded hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function HashCopiable({ hash }: { hash: string }) {
  async function copiar() {
    try {
      await navigator.clipboard.writeText(hash);
      toast.success('Hash copiado al portapapeles');
    } catch {
      toast.error('No se pudo copiar');
    }
  }
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="font-mono text-xs text-gray-500 truncate max-w-[140px] cursor-help"
        title={hash}
      >
        {hash.slice(0, 12)}…
      </span>
      <button
        type="button"
        onClick={copiar}
        title="Copiar hash completo"
        className="text-gray-400 hover:text-blue-600 p-0.5 rounded"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </button>
    </div>
  );
}
